import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatUZS } from '@/lib/format';
import { topProducts, type SaleLike } from '@/lib/report-metrics';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ChartTooltipBox, compactUZS } from './chart-bits';
import { ExportButtons } from './ExportButtons';

export function TopProducts({ sales, isLoading }: { sales: SaleLike[]; isLoading?: boolean }) {
  const { t } = useTranslation();
  const [by, setBy] = useState<'sum' | 'quantity'>('sum');
  const rows = topProducts(sales, by, 10);

  const exportRows = rows.map((r, i) => ({
    n: i + 1,
    name: r.name,
    quantity: r.quantity,
    sum: formatUZS(r.sum),
  }));

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="p-4 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-medium text-foreground">{t('tick_reports.topProducts')}</h3>
        <div className="flex items-center gap-2">
          <ToggleGroup
            type="single"
            value={by}
            onValueChange={(v) => v && setBy(v as 'sum' | 'quantity')}
            className="bg-muted/50 rounded-lg p-0.5"
          >
            <ToggleGroupItem value="sum" className="text-xs px-3 h-7">{t('tick_reports.colSum')}</ToggleGroupItem>
            <ToggleGroupItem value="quantity" className="text-xs px-3 h-7">{t('tick_reports.colQty')}</ToggleGroupItem>
          </ToggleGroup>
          <ExportButtons
            fileName="top-tovarlar"
            title={t('tick_reports.topProducts')}
            columns={[
              { header: '#', key: 'n' },
              { header: t('tick_reports.colProduct'), key: 'name' },
              { header: t('tick_reports.colQty'), key: 'quantity' },
              { header: t('tick_reports.colSum'), key: 'sum' },
            ]}
            rows={exportRows}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="p-6 text-center text-sm text-muted-foreground">{t('tick_reports.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">{t('tick_reports.noData')}</div>
      ) : (
        <>
          <div className="h-[320px] p-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v) => (by === 'sum' ? compactUZS(v) : String(v))}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  width={150}
                  tickFormatter={(v: string) => (v.length > 20 ? `${v.slice(0, 19)}…` : v)}
                />
                <Tooltip
                  cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                  content={({ active, payload }) =>
                    active && payload?.length ? (
                      <ChartTooltipBox
                        title={String(payload[0]?.payload?.name ?? '')}
                        rows={[
                          { label: t('tick_reports.colSum'), value: formatUZS(Number(payload[0]?.payload?.sum ?? 0)) },
                          { label: t('tick_reports.colQty'), value: `${payload[0]?.payload?.quantity ?? 0}` },
                        ]}
                      />
                    ) : null
                  }
                />
                <Bar dataKey={by} fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <table className="w-full">
            <thead>
              <tr className="border-y border-border bg-muted/50">
                <th className="text-left text-xs font-medium text-muted-foreground p-3">#</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.colProduct')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.colQty')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.colSum')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.productId} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="p-3 text-sm text-muted-foreground">{i + 1}</td>
                  <td className="p-3 text-sm text-foreground font-medium">{r.name}</td>
                  <td className="p-3 text-sm text-foreground">{r.quantity}</td>
                  <td className="p-3 text-sm font-medium text-foreground">{formatUZS(r.sum)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
